import { RadioGroupChangeEventDetail } from "@ionic/core";
import { IonRadioGroup } from "@ionic/react";
import classnames from "classnames";
import { ComponentProps, ReactNode } from "react";
import { Control, Controller, FieldError, FieldPath, FieldValues } from "react-hook-form";

import Error from "@components/v1/fields/Error";
import RequiredFieldIndicator from "@components/v1/fields/RequiredFieldIndicator";
import InputLabel from "@components/v1/typography/InputLabel";
import RadioButton from "@components/v2/forms/RadioButton";
import styles from "@components/v2/forms/RadioGroup.module.css";

type Option = ComponentProps<typeof RadioButton>;

type Props<FormData extends FieldValues> = {
  className?: string;
  control: Control<FormData>;
  disabled?: boolean;
  error: FieldError | undefined;
  label?: string | ReactNode;
  labelPlacement?: "end" | "start";
  name: FieldPath<FormData>;
  options: Option[];
  required?: boolean;
};

const RadioGroup = <FormData extends FieldValues>({
  className,
  control,
  disabled = false,
  error,
  label,
  labelPlacement = "end",
  name,
  options,
  required = false
}: Props<FormData>) => (
  <div className={classnames("component-RadioGroup", styles.wrapper, className)}>
    {label && (
      <div className={styles.labelSpacing}>
        <InputLabel>
          {label}
          {required && <RequiredFieldIndicator />}
        </InputLabel>
      </div>
    )}
    <div>
      <Controller
        control={control}
        name={name}
        render={({ field: { name, onBlur, onChange, value } }) => (
          // eslint-disable-next-line react/forbid-elements
          <IonRadioGroup
            className={styles.items}
            name={name}
            onIonChange={(event: CustomEvent<RadioGroupChangeEventDetail>) => {
              onChange(event.detail.value);
            }}
            value={value}
          >
            {options.map(option => (
              <div className={styles.item} key={option.value}>
                <RadioButton disabled={disabled} labelPlacement={labelPlacement} onIonBlur={onBlur} {...option} />
              </div>
            ))}
          </IonRadioGroup>
        )}
      />
    </div>
    {error && <Error error={error} />}
  </div>
);

export default RadioGroup;
